import { useEffect, useState } from "react";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useLocation, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetFooter, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { TourCard } from "@/components/site/TourCard";
import { FilterSidebar } from "@/components/site/FilterSidebar";
import { TestimonialsAndFollowSection } from "@/components/site/TrustSection";
import { HomeIntro } from "@/components/site/HomeIntro";
import { getTours, getRegionCounts } from "@/lib/api";

export default function Home() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [tours, setTours] = useState([]);
  const [regionCounts, setRegionCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState(() => searchParams.get("region") ? [searchParams.get("region")] : []);
  const [selectedDurations, setSelectedDurations] = useState([]);
  const [selectedTiers, setSelectedTiers] = useState([]);
  const isHome = location.pathname === "/";
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  useEffect(() => {
    const region = new URLSearchParams(location.search).get("region");
    setSelectedRegions(region ? [region] : []);
    setSelectedDurations([]);
    setSelectedTiers([]);
  }, [location.key, location.search]);

  useEffect(() => {
    getRegionCounts().then(setRegionCounts).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    const params = {};
    if (selectedRegions.length) params.region = selectedRegions.join(",");
    if (selectedDurations.length) params.duration_bucket = selectedDurations.join(",");
    if (selectedTiers.length) params.tier = selectedTiers.join(",");
    getTours(params).then((data) => { if (active) setTours(data); })
      .catch(() => { if (active) setLoadError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selectedRegions, selectedDurations, selectedTiers, retry]);

  const toggleRegion = (region) => setSelectedRegions((previous) => previous.includes(region) ? previous.filter((item) => item !== region) : [...previous, region]);
  const toggleDuration = (duration) => setSelectedDurations((previous) => previous.includes(duration) ? previous.filter((item) => item !== duration) : [...previous, duration]);
  const toggleTier = (tier) => setSelectedTiers((previous) => previous.includes(tier) ? previous.filter((item) => item !== tier) : [...previous, tier]);
  const clearFilters = () => { setSelectedRegions([]); setSelectedDurations([]); setSelectedTiers([]); };
  const activeFilterCount = selectedRegions.length + selectedDurations.length + selectedTiers.length;
  const filterProps = { selectedRegions, toggleRegion, regionCounts, selectedDurations, toggleDuration, selectedTiers, toggleTier, onClear: clearFilters };

  return <div className={isHome ? "landing-page" : "py-10"}>
    {isHome && <HomeIntro />}
    <section id="catalog" className="mx-auto w-full max-w-7xl scroll-mt-28 px-4 pb-16 pt-6 sm:px-6 lg:px-8" data-testid="tour-catalog">
      <div className="mb-5 flex items-center justify-between gap-3">
        <h2 data-testid="catalog-heading" className="font-display text-xl font-semibold text-foreground sm:text-2xl">{!isHome && selectedRegions.length === 1 ? `${selectedRegions[0]} Tour Packages` : "All Tour Packages"}</h2>
        <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" data-testid="catalog-open-filters-button" className="min-h-11 shrink-0 lg:hidden">Filters {activeFilterCount > 0 && <Badge className="ml-1 bg-primary">{activeFilterCount}</Badge>}</Button>
          </SheetTrigger>
          <SheetContent side="bottom" data-testid="catalog-filter-sheet" closeTestId="catalog-close-filters-button" closeLabel="Close filters" className="mobile-filter-sheet">
            <div className="shrink-0 border-b p-5 pr-16"><SheetTitle>Filter tour packages</SheetTitle><SheetDescription className="mt-1">Find your ideal destination, duration and hotel tier.</SheetDescription></div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4">{mobileFiltersOpen && !isDesktop && <FilterSidebar {...filterProps} />}</div>
            <SheetFooter className="mobile-filter-footer flex-row">
              <Button variant="outline" data-testid="catalog-reset-filters-button" onClick={clearFilters}>Reset</Button>
              <Button data-testid="catalog-apply-filters-button" disabled={loading} onClick={() => setMobileFiltersOpen(false)} className="flex-1 bg-primary">{loading ? "Updating…" : `Show ${tours.length} Packages`}</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>
      <div className="grid grid-cols-12 gap-6">
        {isDesktop && <div className="col-span-3"><div className="sticky top-24"><FilterSidebar {...filterProps} /></div></div>}
        <div className="col-span-12 min-w-0 lg:col-span-9">
          {loading ? <div className="space-y-4" data-testid="catalog-loading">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-48 w-full rounded-xl" />)}</div>
            : loadError ? <div data-testid="catalog-error" role="alert" className="rounded-xl border bg-card p-8"><p className="text-muted-foreground">We couldn't load the packages. Please try again.</p><Button data-testid="catalog-retry-button" className="mt-4" onClick={() => setRetry((value) => value + 1)}>Try again</Button></div>
            : tours.length === 0 ? <div data-testid="catalog-empty" className="rounded-xl border bg-card p-8"><p className="text-muted-foreground">No packages match your filters. Try clearing some filters.</p><Button data-testid="catalog-clear-empty-button" className="mt-4" onClick={clearFilters}>Clear filters</Button></div>
            : <div className="grid grid-cols-1 gap-4" data-testid="tour-results-grid">{tours.map((tour) => <TourCard key={tour.id} tour={tour} />)}</div>}
        </div>
      </div>
    </section>
    <TestimonialsAndFollowSection />
  </div>;
}
